import React from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import Report from 'components/Report';
import RenderReadinessProvider from 'contextProviders/RenderReadinessProvider';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import usePrintReady, { REPORT_MAX_WAIT_MS } from 'hooks/usePrintReady';

import useReport from '../ReportShow/useReport';

// The report's own container is a viewport-height scroll container
// (height: calc(100vh - …)), so we DON'T flatten heights (that collapses the
// layout). Instead the renderer grows the viewport to the content height so it
// all fits without scrolling. Here we just strip the body margin and ensure a
// solid background for the capture.
const PrintGlobalStyle = createGlobalStyle`
  body { margin: 0; background: var(--color-bg-primary); }
  /* The scheduled-report PDF is the report content only. Hide the report title
     and the filter/controls bar — both carry the EXCLUDE_HEIGHT (exclude-height-*)
     class — so the first thing in the PDF is the first gadget. The gadget grid
     itself has no such class, so it is unaffected. */
  #print-report-root [class*="exclude-height-"] { display: none !important; }
`;

// Fixed render width for a consistent layout regardless of the runner's viewport.
const PrintRoot = styled.div`
  width: 1280px;
  background: var(--color-bg-primary);
`;

const PrintReportContent = () => {
  // This screen isn't mounted under a :reportId <Route>, so derive the id from the
  // path rather than useParams (which would be empty here).
  const { pathname } = useLocation();
  const reportId = pathname.split('/print/reports/')[1]?.split(/[?/]/)[0];
  const {
    report,
    setReport,
    isLoading: reportLoading,
  } = useReport({ reportId });
  const ready = usePrintReady({
    isContentReady: !reportLoading && !!report,
    maxWaitMs: REPORT_MAX_WAIT_MS,
  });

  return (
    <PrintRoot
      id="print-report-root"
      data-print-ready={ready ? 'true' : undefined}
    >
      <PrintGlobalStyle />
      {report && (
        <WeekStartsOnOverrideContext.Provider
          value={{ weekStartsOnOverride: report.weekStartsOnOverride }}
        >
          <Report
            report={report}
            key={report.id}
            setReport={setReport}
            forceHeadlessRender
          />
        </WeekStartsOnOverrideContext.Provider>
      )}
    </PrintRoot>
  );
};

// Gadgets report their requests to RenderReadinessProvider, which the ready
// check waits on and the renderer reads
const PrintReportShow = () => (
  <RenderReadinessProvider>
    <PrintReportContent />
  </RenderReadinessProvider>
);

export default PrintReportShow;
